import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, Plus, Boxes, ArrowRight, PackageCheck, Link2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import { Button, Spinner } from "../components/ui/kit";
import {
  ZaraditDialog, PolozkaDialog, SkladDialog, InventuraDialog,
  NovyDokladDialog, NaskladnitDialog, PolozkaDetailDialog, PohybRadek,
} from "../components/SkladDialogy";
import SkladDoklad from "../components/SkladDoklad";
import ParovaniCeniku from "../components/ParovaniCeniku";
import { useAuth } from "../context/AuthContext";
import { canSee } from "../lib/nav";
import { fmtKc } from "../lib/format";
import api from "../lib/api";
import { useUrlStav } from "../lib/navigace";
import Segment from "../components/Segment";
import Vyber from "../components/Vyber";

const MESICE = ["lednu", "únoru", "březnu", "dubnu", "květnu", "červnu",
  "červenci", "srpnu", "září", "říjnu", "listopadu", "prosinci"];

function fmtD(d) {
  if (!d) return "—";
  const [y, m, dd] = String(d).split("-");
  return dd ? `${Number(dd)}. ${Number(m)}. ${y}` : d;
}

function Dlazdice({ label, hodnota, popis, tmava, onClick, barva = "text-muted", testid }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} data-testid={testid}
      className={`${tmava ? "rounded-card bg-ink text-white p-4" : "card p-4 text-left"} ${onClick ? "hover:border-accent/40" : ""}`}>
      <div className={`label-up ${tmava ? "text-white/50" : "text-muted"} flex items-center gap-1`}>
        {label}{onClick && <ArrowRight size={11} className="ml-auto text-accent-hover" />}
      </div>
      <div className="amount text-[20px] font-800 mt-1.5">{hodnota}</div>
      <div className={`text-[11.5px] font-600 mt-1 ${tmava ? "text-white/50" : barva}`}>{popis}</div>
    </Tag>
  );
}

export default function Sklad() {
  const { user } = useAuth();
  // Ceník (a s ním párování) vidí jen obchod a vedení.
  const vidiCenik = canSee(user, ["OWNER", "OBCHODNIK"]);
  const [prehled, setPrehled] = useState(null);
  const [sklady, setSklady] = useState([]);
  const [kZarazeni, setKZarazeni] = useState({ items: [] });
  const [ceka, setCeka] = useState({ items: [] });
  const [polozky, setPolozky] = useState(null);
  const [pohyby, setPohyby] = useState(null);

  const [tab, setTab] = useUrlStav("tab", "zasoby");
  const [skladId, setSkladId] = useUrlStav("sklad", "");
  const [q, setQ] = useUrlStav("q", "");
  const [jenPodMinimem, setJenPodMinimem] = useState(false);
  const [dialog, setDialog] = useState(null);   // {typ, doklad, davka, polozkaId}
  const dokladyRef = useRef(null);
  const cekaRef = useRef(null);

  const nactiZakladni = useCallback(async () => {
    const [p, s, z, c] = await Promise.all([
      api.get("/api/sklad/prehled"),
      api.get("/api/sklady"),
      api.get("/api/sklad/k-zarazeni"),
      api.get("/api/sklad/ceka"),
    ]);
    setPrehled(p.data); setSklady(s.data.items || []); setKZarazeni(z.data); setCeka(c.data);
  }, []);

  useEffect(() => { nactiZakladni(); }, [nactiZakladni]);

  const nactiObsah = useCallback(async () => {
    if (tab === "zasoby") {
      setPolozky(null);
      const params = {};
      if (skladId) params.sklad_id = skladId;
      if (q) params.q = q;
      if (jenPodMinimem) params.pod_minimem = 1;
      const { data } = await api.get("/api/sklad/polozky", { params });
      setPolozky(data);
    } else {
      setPohyby(null);
      const params = { typ: tab === "prijmy" ? "PRIJEM" : "VYDEJ,VRATKA,PRESUN" };
      if (skladId) params.sklad_id = skladId;
      const { data } = await api.get("/api/sklad/pohyby", { params });
      setPohyby(data);
    }
  }, [tab, skladId, q, jenPodMinimem]);

  useEffect(() => { nactiObsah(); }, [nactiObsah]);

  const poZmene = () => { nactiZakladni(); nactiObsah(); };

  /** Doklad se po přečtení rovnou otevře k zařazení — kvůli tomu ho uživatel fotil. */
  const dokladPrecten = async (fid) => {
    const { data } = await api.get("/api/sklad/k-zarazeni");
    setKZarazeni(data);
    nactiZakladni();
    const d = (data.items || []).find((x) => x.faktura_id === fid);
    if (d) setDialog({ typ: "zaradit", doklad: d });
  };

  const skladName = skladId ? (sklady.find((s) => s.id === skladId)?.nazev || "") : "všechny sklady";
  const mesic = MESICE[new Date().getMonth()];
  const doklady = kZarazeni.items || [];
  const cekajici = (ceka.items || []).filter((d) => !skladId || d.sklad_id === skladId);

  return (
    <div data-testid="sklad-page">
      <PageHeader eyebrow="Ekonomika" title="Sklad"
        subtitle={sklady.length
          ? `${sklady.length} ${sklady.length === 1 ? "sklad" : "sklady"} · ${doklady.length} ${doklady.length === 1 ? "doklad" : "dokladů"} k zařazení`
          : "Založ si sklad a začni evidovat materiál."}
        actions={
          <>
            <Button variant="ghost" onClick={() => setDialog({ typ: "inventura" })}
              disabled={!sklady.length} data-testid="btn-inventura">Inventura</Button>
            {/* Výdejka, vratka i převodka jsou jeden doklad — typ je uvnitř přepínač. */}
            <Button variant="ghost" onClick={() => setDialog({ typ: "doklad-sklad", doklad: { typ: "VYDEJKA" } })}
              disabled={!sklady.length} data-testid="btn-vydejka">Výdejka</Button>
            <Button onClick={() => setDialog(sklady.length ? { typ: "doklad" } : { typ: "sklad" })}
              data-testid="btn-novy-doklad">
              <Plus size={15} /> {sklady.length ? "Doklad" : "Nový sklad"}
            </Button>
          </>
        } />

      {prehled === null ? (
        <div className="p-10 flex justify-center"><Spinner /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4" data-testid="sklad-kpi">
            <Dlazdice tmava label="Hodnota zásob" hodnota={`${fmtKc(prehled.hodnota_zasob_kc)} Kč`}
              popis={`${prehled.polozek} položek skladem`} testid="kpi-hodnota" />
            <Dlazdice label="Čeká na naskladnění" hodnota={`${fmtKc(prehled.ceka_kc)} Kč`}
              popis={prehled.ceka_polozek ? `${prehled.ceka_polozek} položek k převzetí` : "nic nečeká"}
              barva={prehled.ceka_polozek ? "text-accent-hover" : "text-muted"} testid="kpi-ceka"
              onClick={() => cekaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} />
            <Dlazdice label="Nezařazeno k zakázkám" hodnota={`${fmtKc(prehled.nezarazeno_kc)} Kč`}
              popis={`${prehled.nezarazeno_dokladu} dokladů`} barva="text-accent-hover" testid="kpi-nezarazeno"
              onClick={() => dokladyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} />
            <Dlazdice label="Dochází / pod minimem" hodnota={`${prehled.dochazi_pocet} položek`}
              popis={prehled.dochazi_pocet ? `doplnění za ${fmtKc(prehled.doplneni_kc)} Kč` : "vše nad minimem"}
              barva={prehled.dochazi_pocet ? "text-danger" : "text-muted"} testid="kpi-dochazi"
              onClick={() => { setTab("zasoby"); setJenPodMinimem(true); }} />
            <Dlazdice label={`Vydáno v ${mesic}`} hodnota={`${fmtKc(prehled.vydano_mesic_kc)} Kč`}
              popis={`na ${prehled.vydano_mesic_zakazek} zakázek`} testid="kpi-vydano" />
          </div>

          {/* Ceník je nadřazený skladu — položka mimo ceník drží vlastní, časem zastaralou cenu. */}
          {vidiCenik && prehled.mimo_cenik > 0 && (
            <button onClick={() => setDialog({ typ: "parovani" })} data-testid="sklad-mimo-cenik"
              className="card w-full text-left px-4 py-3 mb-4 flex items-center gap-3 hover:border-accent/40">
              <span className="h-9 w-9 rounded-[11px] bg-accent/15 text-[#a8410c] inline-flex items-center justify-center shrink-0">
                <Link2 size={16} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block t-telo font-700 text-ink">
                  {prehled.mimo_cenik} {prehled.mimo_cenik === 1 ? "položka skladu není" : prehled.mimo_cenik < 5 ? "položky skladu nejsou" : "položek skladu není"} v ceníku
                </span>
                <span className="block t-meta text-muted">Po propojení bere sklad nákupní cenu z ceníku — zásoby i výdejky budou oceněné stejně jako nabídky.</span>
              </span>
              <span className="hidden md:inline t-meta font-700 text-accent-hover shrink-0">Propojit s ceníkem →</span>
            </button>
          )}

          {/* Čeká na naskladnění — papír už dorazil, materiál ještě ne. */}
          <div ref={cekaRef} className="card p-4 mb-4" data-testid="ceka-na-naskladneni">
            <div className="flex items-center gap-2 mb-3">
              <PackageCheck size={16} className="text-accent" />
              <h2 className="text-[14px] font-800 text-ink">Čeká na naskladnění</h2>
              <span className="amount text-[11px] text-muted-2">{cekajici.length}</span>
            </div>
            {cekajici.length === 0 ? (
              <p className="text-[12.5px] text-muted">
                Nic nečeká — všechen zařazený materiál je fyzicky na skladě.
              </p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {cekajici.map((d) => (
                  <button key={d.davka_id} onClick={() => setDialog({ typ: "naskladnit", davka: d })}
                    data-testid={`ceka-${d.davka_id}`}
                    className="border border-line rounded-card p-3.5 text-left hover:border-accent/40">
                    <div className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                      <span className="text-[12.5px] font-700 text-ink truncate">{d.sklad_nazev}</span>
                    </div>
                    <div className="text-[11.5px] text-muted mt-1 truncate">
                      {d.doklad?.cislo ? `${d.doklad.cislo} · ` : ""}{fmtD(d.datum)} · {d.polozek} položek
                    </div>
                    <div className="flex items-baseline justify-between mt-2.5">
                      <span className="amount text-[14px] font-800 text-ink">{fmtKc(d.hodnota_kc)} Kč</span>
                      <span className="text-[11.5px] font-700 text-accent-hover">Naskladnit →</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Doklady k zařazení — dokud tam visí, chybí v nákladech i v zásobách. */}
          <div ref={dokladyRef} className="card p-4 mb-4" data-testid="doklady-k-zarazeni">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-[14px] font-800 text-ink">Doklady k zařazení</h2>
              <span className="amount text-[11px] text-muted-2">{doklady.length}</span>
              <button className="ml-auto text-[12px] font-700 text-accent-hover"
                onClick={() => setDialog({ typ: "doklad" })} data-testid="vyfotit-doklad">
                Vyfotit doklad →
              </button>
            </div>
            {doklady.length === 0 ? (
              <p className="text-[12.5px] text-muted">
                Všechny přijaté doklady jsou zařazené — buď na zakázku, nebo na sklad.
              </p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {doklady.map((d) => (
                  <div key={d.faktura_id} className="border border-line rounded-card p-3.5"
                    data-testid={`doklad-${d.faktura_id}`}>
                    <div className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                      <span className="text-[12.5px] font-700 text-ink truncate">
                        {d.druh === "DODACI_LIST" ? "Dodací list" : "Faktura"} {d.cislo || "bez čísla"}
                      </span>
                    </div>
                    <div className="text-[11.5px] text-muted mt-1 truncate">
                      {d.dodavatel || "neznámý dodavatel"} · {fmtD(d.datum)} · {d.pocet_radku} položek
                    </div>
                    <div className="flex items-baseline justify-between mt-2.5">
                      <span className="amount text-[14px] font-800 text-ink">{fmtKc(d.hodnota_kc)} Kč</span>
                      <button className="text-[11.5px] font-700 text-accent-hover"
                        onClick={() => setDialog({ typ: "zaradit", doklad: d })}
                        data-testid={`zaradit-${d.faktura_id}`}>Zařadit →</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* přepínače */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Segment className="filtr-pas">
              {[["zasoby", "Zásoby"], ["prijmy", "Příjmy"], ["vydejky", "Výdeje a vratky"]].map(([v, l]) => (
                <button data-on={tab === v} key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
                  className={`filtr-seg ${tab === v ? "text-white" : "text-sec"}`}>{l}</button>
              ))}
            </Segment>
            <Vyber className="field h-9 w-auto text-[12.5px]" value={skladId}
              onChange={(e) => setSkladId(e.target.value)} data-testid="sklad-filtr">
              <option value="">Všechny sklady</option>
              {sklady.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nazev}{s.typ === "DODAVKA" ? " (dodávka)" : ""} · {fmtKc(s.hodnota_kc)} Kč
                </option>
              ))}
            </Vyber>
            {skladId && (
              <button className="tap text-[12px] font-700 text-sec hover:text-ink"
                onClick={() => setDialog({ typ: "sklad", sklad: sklady.find((s) => s.id === skladId) })}
                data-testid="btn-upravit-sklad">Upravit sklad</button>
            )}
            <button className="text-[12px] font-700 text-accent-hover" onClick={() => setDialog({ typ: "sklad" })}
              data-testid="btn-novy-sklad">+ Sklad</button>
            {tab === "zasoby" && (
              <div className="relative ml-auto">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="EAN / kód / název…"
                  className="field pl-8 h-9 w-[220px] text-[13px]" data-testid="sklad-hledat" />
              </div>
            )}
          </div>

          {tab === "zasoby" && jenPodMinimem && (
            <button onClick={() => setJenPodMinimem(false)} data-testid="filtr-pod-minimem"
              className="card w-full text-left px-4 py-3 mb-3 flex items-center gap-3 hover:bg-inputbg">
              <span className="h-2 w-2 rounded-full bg-danger shrink-0" />
              <span className="text-[13px] font-700 text-ink">Jen položky pod minimem</span>
              <span className="text-[12px] text-muted">doplnění za <span className="amount">{fmtKc(prehled.doplneni_kc)} Kč</span></span>
              <span className="ml-auto text-[12px] font-600 text-accent-hover">Zrušit filtr</span>
            </button>
          )}

          {tab === "zasoby" ? (
            <div className="card overflow-hidden" data-testid="tabulka-zasoby">
              {polozky === null ? (
                <div className="p-10 flex justify-center"><Spinner /></div>
              ) : !(polozky.items || []).length ? (
                <EmptyState icon="Boxes" title="Zatím nic na skladě"
                  description="Vyfoť dodací list nebo fakturu a zařaď z ní položky na sklad." />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px]">
                    <thead>
                      <tr className="bg-inputbg border-b border-line-soft label-up text-muted-2">
                        <th className="text-left font-600 px-4 py-2.5">Položka / kód</th>
                        <th className="text-left font-600 px-4 py-2.5">Umístění</th>
                        <th className="text-right font-600 px-4 py-2.5">Na skladě</th>
                        <th className="text-right font-600 px-4 py-2.5">Nákup</th>
                        <th className="text-right font-600 px-4 py-2.5">Hodnota</th>
                        <th className="text-right font-600 px-4 py-2.5">Stav</th>
                      </tr>
                    </thead>
                    <tbody>
                      {polozky.items.map((p) => (
                        <tr key={p.id} className="border-b border-line-softer last:border-0 hover:bg-inputbg cursor-pointer"
                          onClick={() => setDialog({ typ: "polozka-detail", polozkaId: p.id })}
                          data-testid={`polozka-${p.id}`}>
                          <td className="px-4 py-3">
                            <div className="text-[13px] font-600 text-ink truncate">{p.nazev}</div>
                            <div className="amount text-[10.5px] text-muted-2">{p.kod || p.ean || "—"}</div>
                          </td>
                          <td className="px-4 py-3 text-[12px] text-sec-3">{p.umisteni || "—"}</td>
                          <td className="px-4 py-3 text-right amount text-[13px] font-600 text-ink">
                            {p.zasoba} <span className="text-[11px] text-muted-2">{p.mj}</span>
                          </td>
                          <td className="px-4 py-3 text-right amount text-[12.5px] text-muted"
                            title={p.propojeno ? "Cena z ceníku" : "Položka není v ceníku — drží cenu posledního příjmu"}>
                            {fmtKc(p.cena_nakup)} Kč
                            {!p.propojeno && <div className="text-[10px] font-600 text-accent-hover" data-testid={`mimo-cenik-${p.id}`}>mimo ceník</div>}
                          </td>
                          <td className="px-4 py-3 text-right amount text-[13px] font-700 text-ink">{fmtKc(p.hodnota_kc)} Kč</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {p.ceka > 0 && (
                              <span className="label-up px-2 py-1 rounded-pill bg-accent/10 text-accent-hover mr-1">
                                čeká {p.ceka} {p.mj}
                              </span>
                            )}
                            {(() => {
                              const s = p.stav_minima;
                              const m = s && {
                                ok: ["v pořádku", "bg-success-bg text-success"],
                                near: ["blíží se minimu", "bg-[#fbf3e2] text-[#c98a13]"],
                                below: ["pod minimem", "bg-[#fdeeec] text-danger"],
                              }[s.status];
                              return (
                                <span className={`label-up px-2 py-1 rounded-pill ${m ? m[1] : "bg-success-bg text-success"}`}
                                  data-testid={`stav-${p.id}`} title={s ? s.diff : ""}>
                                  {m ? m[0] : "v pořádku"}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="px-4 py-3 bg-inputbg flex items-center gap-3">
                <button className="text-[12px] font-700 text-accent-hover" onClick={() => setDialog({ typ: "polozka" })}
                  data-testid="btn-nova-polozka">+ Nová položka</button>
                <span className="ml-auto text-[11.5px] text-muted-2">
                  {(polozky?.items || []).length} položek · {skladName}
                </span>
              </div>
            </div>
          ) : (
            <div className="card overflow-hidden" data-testid={`tabulka-${tab}`}>
              {pohyby === null ? (
                <div className="p-10 flex justify-center"><Spinner /></div>
              ) : !(pohyby.items || []).length ? (
                <EmptyState icon="Boxes"
                  title={tab === "prijmy" ? "Zatím žádné příjmy" : "Zatím žádné výdeje"}
                  description={tab === "prijmy"
                    ? "Naskladněný materiál se objeví tady i s odkazem na doklad."
                    : "Výdejkou se materiál stává nákladem zakázky, vratkou se z nákladů vrací."} />
              ) : pohyby.items.map((p) => (
                <PohybRadek key={p.id} p={p}
                  onOtevri={() => setDialog({ typ: "polozka-detail", polozkaId: p.polozka_id })} />
              ))}
              <div className="px-4 py-3 bg-inputbg flex items-center">
                <span className="label-up text-muted-2">Celkem</span>
                <span className="ml-auto amount text-[13px] font-800 text-ink">{fmtKc(pohyby?.hodnota_kc || 0)} Kč</span>
              </div>
            </div>
          )}

          {!sklady.length && (
            <div className="card p-6 mt-4 flex items-center gap-3" data-testid="sklad-prazdno">
              <Boxes size={20} className="text-muted shrink-0" />
              <div className="min-w-0">
                <div className="text-[13.5px] font-700 text-ink">Ještě nemáš žádný sklad</div>
                <div className="text-[12px] text-muted mt-0.5">
                  Založ centrálu a dodávky techniků — materiál v autě je pořád tvůj majetek.
                </div>
              </div>
              <Button className="ml-auto shrink-0" onClick={() => setDialog({ typ: "sklad" })}>Založit sklad</Button>
            </div>
          )}
        </>
      )}

      {dialog?.typ === "doklad" && <NovyDokladDialog onClose={() => setDialog(null)} onHotovo={dokladPrecten} />}
      {dialog?.typ === "doklad-sklad" && (
        <SkladDoklad sklady={sklady} vychozi={dialog.doklad}
          onClose={() => { setDialog(null); poZmene(); }} onHotovo={poZmene} />
      )}
      {dialog?.typ === "parovani" && <ParovaniCeniku onClose={() => setDialog(null)} onSaved={poZmene} />}
      {dialog?.typ === "inventura" && <InventuraDialog sklady={sklady} onClose={() => setDialog(null)} onSaved={poZmene} />}
      {dialog?.typ === "polozka" && <PolozkaDialog onClose={() => setDialog(null)} onSaved={poZmene} />}
      {dialog?.typ === "sklad" && (
        <SkladDialog sklad={dialog.sklad || null} onClose={() => setDialog(null)}
          onSaved={(r) => { if (r?.smazano) setSkladId(""); poZmene(); }} />
      )}
      {dialog?.typ === "zaradit" && (
        <ZaraditDialog doklad={dialog.doklad} sklady={sklady} onClose={() => setDialog(null)} onSaved={poZmene} />
      )}
      {dialog?.typ === "naskladnit" && (
        <NaskladnitDialog davka={dialog.davka} onClose={() => setDialog(null)} onSaved={poZmene} />
      )}
      {dialog?.typ === "polozka-detail" && (
        <PolozkaDetailDialog polozkaId={dialog.polozkaId} skladId={skladId} sklady={sklady}
          onClose={() => setDialog(null)}
          onPresun={(p) => setDialog({ typ: "doklad-sklad", doklad: { typ: "PREVODKA", ...p } })}
          onVratka={(p) => setDialog({ typ: "doklad-sklad", doklad: { typ: "VRATKA", ...p } })}
          onZmena={poZmene} />
      )}
    </div>
  );
}
